import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Button } from 'components/UIElements'
import { logSentryEvent, SENTRY_MESSAGES } from 'utils/sentry'

const WebcamCapture = ({
  cameraTip,
  onCancel,
  onCapture,
  strings = {},
}) => {
  const videoRef = useRef(null)
  const streamRef = useRef(null)
  const [errorMessage, setErrorMessage] = useState('')
  const [isReady, setIsReady] = useState(false)
  const [isCapturing, setIsCapturing] = useState(false)

  useEffect(() => {
    let cancelled = false

    const startCamera = async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setErrorMessage(
          strings.camera_not_supported ||
            'Camera capture is not supported in this browser.',
        )
        return
      }

      try {
        let stream
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            audio: false,
            video: { facingMode: { ideal: 'environment' } },
          })
        } catch (_) {
          stream = await navigator.mediaDevices.getUserMedia({
            audio: false,
            video: true,
          })
        }

        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop())
          return
        }

        streamRef.current = stream
        const video = videoRef.current
        if (!video) return

        video.srcObject = stream
        video.onloadedmetadata = () => {
          video
            .play()
            .then(() => {
              if (!cancelled) setIsReady(true)
            })
            .catch(() => {
              if (!cancelled) {
                setErrorMessage(
                  strings.camera_start_failed ||
                    'Unable to start the camera preview.',
                )
              }
            })
        }

        logSentryEvent({
          message: SENTRY_MESSAGES.permissionAccepted,
          tags: {
            flow: 'permissions',
            action: 'accepted',
            permission_type: 'camera',
          },
        })
      } catch (error) {
        if (cancelled) return
        const denied =
          error?.name === 'NotAllowedError' ||
          error?.name === 'PermissionDeniedError'
        logSentryEvent({
          message: denied
            ? SENTRY_MESSAGES.permissionDenied
            : SENTRY_MESSAGES.permissionError,
          level: denied ? 'info' : 'error',
          tags: {
            flow: 'permissions',
            action: denied ? 'denied' : 'error',
            permission_type: 'camera',
          },
          error: denied ? undefined : error,
        })
        setErrorMessage(
          strings.photo_submission_requires_camera_help ||
            'Please allow camera access for this browser and try again.',
        )
      }
    }

    startCamera()

    return () => {
      cancelled = true
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop())
        streamRef.current = null
      }
    }
  }, [strings])

  const handleCapture = () => {
    const video = videoRef.current
    if (!video || !isReady || isCapturing) return

    setIsCapturing(true)
    const canvas = document.createElement('canvas')
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    const context = canvas.getContext('2d')
    context.drawImage(video, 0, 0, canvas.width, canvas.height)

    canvas.toBlob(
      (blob) => {
        setIsCapturing(false)
        if (!blob) {
          setErrorMessage(
            strings.camera_capture_failed ||
              'Unable to capture the photo. Please try again.',
          )
          return
        }

        const file = new File([blob], `capture-${Date.now()}.jpeg`, {
          type: 'image/jpeg',
          lastModified: Date.now(),
        })
        onCapture(file)
      },
      'image/jpeg',
      0.92,
    )
  }

  return (
    <div className='webcam-capture flexed column center-aligned'>
      {errorMessage ? (
        <p className='webcam-capture__error'>{errorMessage}</p>
      ) : (
        <>
          <video
            ref={videoRef}
            className='webcam-capture__video'
            autoPlay
            muted
            playsInline
          />
          {cameraTip ? <p className='webcam-capture__tip'>{cameraTip}</p> : null}
        </>
      )}
      <div className='webcam-capture__actions flexed'>
        <Button link content={strings.cancel_btn || 'Cancel'} onClick={onCancel} />
        {!errorMessage ? (
          <Button
            content={strings.take_photo || 'Take Photo'}
            disabled={!isReady || isCapturing}
            onClick={handleCapture}
          />
        ) : null}
      </div>
    </div>
  )
}

WebcamCapture.propTypes = {
  cameraTip: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  onCapture: PropTypes.func.isRequired,
  strings: PropTypes.objectOf(PropTypes.string),
}

export default WebcamCapture
